/**
 * Reports Hook
 *
 * Provides data fetching and mutations for reports
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { reportingService } from '@/services/reporting.service';
import toast from 'react-hot-toast';
import type {
  ListReportsQuery,
  ListReportsResponse,
  OrganizationOverviewRequest,
  DepartmentPerformanceReportRequest,
} from '@/types/reporting-v2';

export function useReports(
  query: ListReportsQuery,
  options?: Omit<UseQueryOptions<ListReportsResponse['data']>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['reports', 'list', query],
    queryFn: () => reportingService.listReports(query),
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

export function useReport(
  snapshotId: string | undefined,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['reports', snapshotId],
    queryFn: () => reportingService.getReport(snapshotId!),
    enabled: !!snapshotId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useGenerateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { type: string; data: any }) => {
      switch (params.type) {
        case 'organization_overview':
          return reportingService.generateOrganizationOverview(params.data as OrganizationOverviewRequest);
        case 'department_performance':
          return reportingService.generateDepartmentPerformance(params.data as DepartmentPerformanceReportRequest);
        case 'role_analysis':
          return reportingService.generateRoleAnalysis(params.data);
        case 'course_effectiveness':
          return reportingService.generateCourseEffectiveness(params.data);
        case 'learner_progress':
          return reportingService.generateLearnerProgressReport(params.data);
        case 'certifications':
          return reportingService.generateCertificationsReport(params.data);
        case 'learning_path':
          return reportingService.generateLearningPathReport(params.data);
        default:
          throw new Error('Invalid report type');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', 'list'] });
      toast.success('Report generated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to generate report');
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (snapshotId: string) => reportingService.deleteReport(snapshotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', 'list'] });
      toast.success('Report deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete report');
    },
  });
}

export function useExportReport() {
  return useMutation({
    mutationFn: async ({ snapshotId, format }: { snapshotId: string; format: 'pdf' | 'excel' }) => {
      const blob =
        format === 'pdf'
          ? await reportingService.exportReportPDF(snapshotId)
          : await reportingService.exportReportExcel(snapshotId);

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${snapshotId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    onSuccess: (_, variables) => {
      toast.success(`Report exported as ${variables.format.toUpperCase()}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to export report');
    },
  });
}
